'use client';

import {
  Button,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';

import type {
  FeedbackRecipient,
  RecipientImportPreview,
} from '@/core/lib/parse-feedback-recipients';

export type FeedbackRecipientImportPreviewLabels = {
  title: string;
  confirm: string;
  cancel: string;
  ready: string;
  invalid: string;
  duplicate: string;
  overQuota: string;
};

function recipientLine(entry: FeedbackRecipient): string {
  return entry.name ? `${entry.name} · ${entry.email}` : entry.email;
}

function PreviewGroup({
  label,
  rows,
}: {
  label: string;
  rows: FeedbackRecipient[];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">
        {label.replace('{count}', String(rows.length))}
      </p>
      <ul className="max-h-40 space-y-1 overflow-y-auto">
        {rows.map((row) => (
          <li
            key={`${row.email}-${row.name ?? ''}`}
            className="truncate text-sm"
          >
            {recipientLine(row)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FeedbackRecipientImportPreview({
  preview,
  labels,
  onConfirm,
  onCancel,
}: {
  preview: RecipientImportPreview | null;
  labels: FeedbackRecipientImportPreviewLabels;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const open = preview != null;
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-md">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{labels.title}</ResponsiveDialogTitle>
        </ResponsiveDialogHeader>
        {preview ? (
          <div className="space-y-4 px-1 py-2">
            <PreviewGroup label={labels.ready} rows={preview.ready} />
            <PreviewGroup label={labels.invalid} rows={preview.invalid} />
            <PreviewGroup label={labels.duplicate} rows={preview.duplicate} />
            <PreviewGroup label={labels.overQuota} rows={preview.overQuota} />
          </div>
        ) : null}
        <ResponsiveDialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {labels.cancel}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!preview || preview.ready.length === 0}
          >
            {labels.confirm}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
